import { Link } from 'react-router-dom';

import styles from './LandingPage.module.css';

import { PROFILES } from '@/features/auth/profiles';

/**
 * Página pública de apresentação do ObraLink.
 *
 * <p>
 * É a raiz "/", aberta a quem não tem sessão. O caminho de conversão é o
 * cadastro <b>por perfil</b>: cada botão leva a <code>/cadastro?perfil=…</code>,
 * e a tela de cadastro já abre no perfil escolhido. Quem já tem conta entra pelo
 * topo.
 * </p>
 * <p>
 * Sem imagem externa: a arte é SVG inline, para não violar o CSP nem depender de
 * CDN. Todas as cores vêm dos tokens do design system.
 * </p>
 */
export function LandingPage(): JSX.Element {
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.bar}>
          <Link className={styles.brand} to="/">
            <span aria-hidden="true" className={styles.brandMark}>
              <BrandGlyph />
            </span>
            ObraLink
          </Link>

          <nav className={styles.nav}>
            <a className={styles.navLink} href="#recursos">
              Recursos
            </a>
            <a className={styles.navLink} href="#como-funciona">
              Como funciona
            </a>
            <Link className={styles.navLink} to="/entrar">
              Entrar
            </Link>
            <Link className={`${styles.btn} ${styles.btnPrimary}`} to="/cadastro">
              Criar conta
            </Link>
          </nav>
        </div>
      </header>

      <main id="conteudo">
        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <section className={styles.hero}>
          <div className={styles.heroInner}>
            <div>
              <span className={styles.eyebrow}>
                <span aria-hidden="true" className={styles.dot} />
                Feito para a construção civil
              </span>

              <h1 className={styles.title}>
                O elo entre quem <span className={styles.accentText}>constrói</span> e quem{' '}
                <span className={styles.accentText}>fornece</span>.
              </h1>

              <p className={styles.lead}>
                Encontre fornecedores de material e serviço, peça e compare cotações e feche negócio
                — tudo num lugar só, entre empresas com CNPJ verificado.
              </p>

              <div className={styles.ctaRow}>
                {PROFILES.map((profile, index) => (
                  <Link
                    className={`${styles.btn} ${index === 0 ? styles.btnPrimary : styles.btnGhost}`}
                    key={profile.value}
                    to={`/cadastro?perfil=${profile.slug}`}
                  >
                    {profile.cta}
                  </Link>
                ))}
              </div>

              <p className={styles.ctaNote}>
                Já tem conta? <Link to="/entrar">Entrar</Link>. Leva menos de um minuto.
              </p>
            </div>

            <div aria-hidden="true" className={styles.heroArt}>
              <BlueprintArt />
            </div>
          </div>
        </section>

        {/* ── Recursos ─────────────────────────────────────────────────── */}
        <section className={styles.section} id="recursos">
          <div className={styles.sectionHead}>
            <p className={styles.kicker}>Por que ObraLink</p>
            <h2 className={styles.sectionTitle}>Menos planilha e corrente de e-mail. Mais obra.</h2>
            <p className={styles.sectionLead}>
              Comprar e vender para a construção é cheio de retrabalho. O ObraLink junta busca,
              cotação e decisão num fluxo só.
            </p>
          </div>

          <div className={styles.features}>
            {FEATURES.map((feature) => (
              <article className={styles.card} key={feature.title}>
                <span aria-hidden="true" className={styles.cardIcon}>
                  {feature.icon}
                </span>
                <h3 className={styles.cardTitle}>{feature.title}</h3>
                <p className={styles.cardText}>{feature.text}</p>
              </article>
            ))}
          </div>
        </section>

        {/* ── Como funciona ────────────────────────────────────────────── */}
        <section className={styles.section} id="como-funciona">
          <div className={styles.sectionHead}>
            <p className={styles.kicker}>Como funciona</p>
            <h2 className={styles.sectionTitle}>Do cadastro ao negócio, em três passos.</h2>
          </div>

          <ol className={styles.steps}>
            {STEPS.map((step, index) => (
              <li className={styles.step} key={step.title}>
                <span aria-hidden="true" className={styles.stepNum}>
                  {index + 1}
                </span>
                <h3 className={styles.stepTitle}>{step.title}</h3>
                <p className={styles.stepText}>{step.text}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* ── Perfis (cadastro por tipo) ───────────────────────────────── */}
        <section className={styles.section} id="perfis">
          <div className={styles.sectionHead}>
            <p className={styles.kicker}>Seu perfil</p>
            <h2 className={styles.sectionTitle}>Escolha como você atua e comece.</h2>
            <p className={styles.sectionLead}>
              O cadastro se adapta ao seu perfil. Você pode atuar dos dois lados.
            </p>
          </div>

          <div className={styles.profiles}>
            {PROFILES.map((profile) => (
              <article className={styles.profile} key={profile.value}>
                <h3 className={styles.profileTitle}>{profile.label}</h3>
                <p className={styles.profileText}>{profile.tagline}</p>
                <Link
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  to={`/cadastro?perfil=${profile.slug}`}
                >
                  {profile.cta}
                </Link>
              </article>
            ))}
          </div>
        </section>
      </main>

      {/* ── Chamada final ──────────────────────────────────────────────── */}
      <section className={styles.cta}>
        <div className={styles.ctaInner}>
          <h2 className={styles.ctaTitle}>
            Pronto para encontrar (ou virar) o próximo fornecedor?
          </h2>
          <div className={styles.ctaCenterRow}>
            <Link className={`${styles.btn} ${styles.btnPrimary}`} to="/cadastro">
              Criar conta grátis
            </Link>
            <Link className={`${styles.btn} ${styles.btnGhost}`} to="/entrar">
              Já tenho conta
            </Link>
          </div>
        </div>
      </section>

      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          <span>© {new Date().getFullYear()} ObraLink</span>
          <span>Plataforma para a construção civil</span>
        </div>
      </footer>
    </div>
  );
}

interface Feature {
  readonly title: string;
  readonly text: string;
  readonly icon: JSX.Element;
}

const FEATURES: readonly Feature[] = [
  {
    title: 'Fornecedores certos',
    text: 'Busque por material, serviço e região. Só empresas com CNPJ, sem pessoa física no meio.',
    icon: <IconSearch />,
  },
  {
    title: 'Cotações num lugar só',
    text: 'Peça propostas e compare lado a lado, sem espalhar pedido por WhatsApp e e-mail.',
    icon: <IconQuote />,
  },
  {
    title: 'Decida com confiança',
    text: 'Perfil completo e avaliações de quem já comprou ajudam a escolher sem apostar.',
    icon: <IconShield />,
  },
  {
    title: 'Pensado para o canteiro',
    text: 'Fluxo direto e objetivo, para resolver entre uma etapa e outra da obra.',
    icon: <IconCrane />,
  },
];

interface Step {
  readonly title: string;
  readonly text: string;
}

const STEPS: readonly Step[] = [
  {
    title: 'Cadastre sua empresa',
    text: 'Escolha seu perfil, informe o CNPJ e crie a conta. Um minuto, sem burocracia.',
  },
  {
    title: 'Busque ou apareça',
    text: 'Comprador procura e pede cotação; fornecedor mostra o que vende e para onde atende.',
  },
  {
    title: 'Feche negócio',
    text: 'Compare propostas, converse e contrate — com o histórico registrado na plataforma.',
  },
];

/* ── Ícones (SVG inline, traço, herdam currentColor) ─────────────────────── */
function IconSearch(): JSX.Element {
  return (
    <svg
      fill="none"
      height="22"
      stroke="currentColor"
      strokeWidth="1.8"
      viewBox="0 0 24 24"
      width="22"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.2-3.2" strokeLinecap="round" />
    </svg>
  );
}

function IconQuote(): JSX.Element {
  return (
    <svg
      fill="none"
      height="22"
      stroke="currentColor"
      strokeWidth="1.8"
      viewBox="0 0 24 24"
      width="22"
    >
      <rect height="17" rx="2" width="13" x="5" y="3.5" />
      <path d="M8.5 8h6M8.5 12h6M8.5 16h3.5" strokeLinecap="round" />
    </svg>
  );
}

function IconShield(): JSX.Element {
  return (
    <svg
      fill="none"
      height="22"
      stroke="currentColor"
      strokeWidth="1.8"
      viewBox="0 0 24 24"
      width="22"
    >
      <path d="M12 3l7 2.5V11c0 4.6-3 8-7 9.5C8 19 5 15.6 5 11V5.5L12 3Z" strokeLinejoin="round" />
      <path d="m9 11.5 2 2 4-4.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconCrane(): JSX.Element {
  return (
    <svg
      fill="none"
      height="22"
      stroke="currentColor"
      strokeWidth="1.8"
      viewBox="0 0 24 24"
      width="22"
    >
      <path
        d="M5 21h9M7 21V6m0 0h11M7 6 4.5 8.5M18 6v3m-6-3-1 3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BrandGlyph(): JSX.Element {
  return (
    <svg
      fill="none"
      height="18"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width="18"
    >
      <path d="M4 20V7l8-3 8 3v13" strokeLinejoin="round" />
      <path d="M10 20v-6h4v6" strokeLinejoin="round" />
    </svg>
  );
}

/** Arte abstrata do hero: uma malha de planta baixa sobre o azul da marca. */
function BlueprintArt(): JSX.Element {
  return (
    <svg fill="none" preserveAspectRatio="xMidYMid slice" viewBox="0 0 400 320">
      <g stroke="white" strokeOpacity="0.18" strokeWidth="1">
        {Array.from({ length: 9 }, (_, i) => (
          <line key={`v${i}`} x1={i * 50} x2={i * 50} y1="0" y2="320" />
        ))}
        {Array.from({ length: 7 }, (_, i) => (
          <line key={`h${i}`} x1="0" x2="400" y1={i * 50} y2={i * 50} />
        ))}
      </g>
      <g stroke="white" strokeOpacity="0.85" strokeWidth="2.5" strokeLinejoin="round" fill="none">
        <path d="M60 250V120l90-40 90 40v130" />
        <path d="M150 250v-70h50v70" />
        <path d="M85 150h35v35H85zM180 150h35v35h-35z" strokeOpacity="0.6" />
        <path d="M240 250V150l70-28 40 22v106" strokeOpacity="0.55" />
      </g>
      <g fill="white">
        <circle cx="150" cy="80" r="4" />
        <circle cx="310" cy="122" r="3" fillOpacity="0.7" />
      </g>
    </svg>
  );
}
